import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BranchCode, StepIdentifier } from 'api/risk';
import { GenericLoading } from 'components/ui/Loader';
import { risksAPI } from 'services/swagger-api/riskApi';
import {
  buildRoute,
  getStepRouteByStepIdentifierString
} from 'features/subscription-process/utils/getRouteByStepIdentifier';

/**
 * Route « dossier » : /simulation/dossier/:riskId
 *
 * Résout l'étape la plus avancée du dossier (branche + step via le back) puis redirige
 * vers la route de cette étape. Permet de naviguer vers un dossier par simple URL
 * (recherche globale, liens partageables…) — la résolution est déclenchée par la route,
 * pas par l'appelant. Montée sous /simulation/* : gating rôle (SubscriptionProcessAccess)
 * et permission SIMU_READ déjà appliqués par les gardes parents.
 */
export const RiskDossierResolver: React.FC = () => {
  const { riskId } = useParams<{ riskId: string }>();
  const navigate = useNavigate();
  const hasResolved = useRef(false);

  useEffect(() => {
    if (!riskId || hasResolved.current) return;
    hasResolved.current = true;

    const resolve = async () => {
      try {
        const response = await risksAPI.getSubscriptionProcess({ riskId });
        const step = response?.subscriptionState?.highestAccessibleStep;
        const routeConfig = step
          ? getStepRouteByStepIdentifierString(response.branchCode, step)
          : undefined;
        if (!step || !routeConfig) {
          toast.error('Impossible de déterminer l’étape de ce dossier.');
          navigate('/mes-simulations', { replace: true });
          return;
        }

        const isContractualization =
          step === StepIdentifier.Contractualization ||
          step === StepIdentifier.SignedContract;
        const quoteId = isContractualization
          ? response.subscriptionState.steps.find(
              (s) => s.identifier === StepIdentifier.Contractualization
            )?.subSteps[0]?.data?.quoteId || ''
          : undefined;

        navigate(buildRoute(routeConfig.route, { riskId, ...(quoteId && { quoteId }) }), {
          replace: true
        });
      } catch (error) {
        console.error('Résolution du dossier impossible:', error);
        toast.error('Dossier introuvable ou inaccessible.');
        navigate('/mes-simulations', { replace: true });
      }
    };

    void resolve();
  }, [riskId, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <GenericLoading loading isParentSized />
    </div>
  );
};

/**
 * Route « dossier emprunteur » : /simulation/dossier-emprunteur/:borrowerProjectId/:borrowerId?
 *
 * Les dossiers EMPRUNTEUR ont une logique d'URL différente : le projet d'emprunt
 * (borrowerProjectId) porte plusieurs risques (borrowerId), et la résolution passe par
 * getSubscriptionProcessBorrower — même mécanique que goToBorrowerSimulation des listes.
 */
export const BorrowerDossierResolver: React.FC = () => {
  const { borrowerProjectId, borrowerId } = useParams<{
    borrowerProjectId: string;
    borrowerId?: string;
  }>();
  const navigate = useNavigate();
  const hasResolved = useRef(false);

  useEffect(() => {
    if (!borrowerProjectId || hasResolved.current) return;
    hasResolved.current = true;

    const resolve = async () => {
      try {
        const response = await risksAPI.getSubscriptionProcessBorrower({
          projetEmpruntId: borrowerProjectId
        });
        const step = response?.subscriptionState?.highestAccessibleStep;
        const routeConfig = step
          ? getStepRouteByStepIdentifierString(BranchCode.Borrower, step)
          : undefined;
        if (!step || !routeConfig) {
          toast.error('Impossible de déterminer l’étape de ce dossier emprunteur.');
          navigate('/mes-simulations', { replace: true });
          return;
        }

        const isContractualization =
          step === StepIdentifier.Contractualization ||
          step === StepIdentifier.SignedContract;
        // Projet multi-emprunteurs : une sous-étape par emprunteur — on prend celle
        // de l'emprunteur de l'URL si fourni, sinon la première qui porte un devis.
        const contractualizationSubSteps =
          response.subscriptionState.steps.find(
            (s) => s.identifier === StepIdentifier.Contractualization
          )?.subSteps ?? [];
        const quoteId = isContractualization
          ? (borrowerId &&
              contractualizationSubSteps.find((ss) => ss.data?.borrowerId === borrowerId)
                ?.data?.quoteId) ||
            contractualizationSubSteps.find((ss) => ss.data?.quoteId)?.data?.quoteId ||
            ''
          : undefined;

        // Contractualisation sans devis identifiable (:quoteId requis) : on retombe
        // sur le tarificateur du projet plutôt que d'échouer.
        const targetRoute =
          isContractualization && !quoteId
            ? getStepRouteByStepIdentifierString(BranchCode.Borrower, StepIdentifier.Pricing)
            : routeConfig;
        if (!targetRoute) {
          toast.error('Impossible de déterminer l’étape de ce dossier emprunteur.');
          navigate('/mes-simulations', { replace: true });
          return;
        }

        navigate(
          buildRoute(targetRoute.route, {
            borrowerProjectId,
            ...(quoteId && { quoteId }),
            ...(borrowerId && { borrowerId })
          }),
          { replace: true }
        );
      } catch (error) {
        console.error('Résolution du dossier emprunteur impossible:', error);
        toast.error('Dossier emprunteur introuvable ou inaccessible.');
        navigate('/mes-simulations', { replace: true });
      }
    };

    void resolve();
  }, [borrowerProjectId, borrowerId, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <GenericLoading loading isParentSized />
    </div>
  );
};

export default RiskDossierResolver;
